import React from 'react';
import {
    Box,
    Checkbox,
    Chip,
    Collapse,
    IconButton,
    TableCell,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { ExpandLess, ExpandMore, Layers, Phone } from '@mui/icons-material';
import InfoIcon from '@mui/icons-material/Info';
import EditIcon from '@mui/icons-material/Edit';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BOOKING_STATUSES, BOOKING_STATUS_COLORS } from '../../../helpers/constants';
import { resolveSlotLabel } from '../../../helpers/slotTemplate';
import { editBookingCheckin } from '@openapi/bookings/bookings';
import useNotify from '../../../hooks/useNotify';
import ExpandableBookingItems from './ExpandableBookingItems';
import { formatCurrency } from '../../bookings/helpers/formatters';
import {
    COL_COUNT,
    formatPhone,
    getTicketCountEntries,
    groupSeatsByTable,
    isMixedBooking,
} from './checkinViewHelpers';

const MIXED_BORDER = '4px solid';
// Purple: distinct from the orange `warning.main` used by PROCESSING status
// chips on the same row, so a mixed booking that's also processing reads as
// two separate signals.
const MIXED_COLOR = '#7e57c2';

function MixedTooltipContent({ ticketTypes }) {
    return (
        <Box>
            {ticketTypes.map((tt) => {
                const opts = (tt.options ?? []).filter((o) => (o.count || 0) > 0);
                return (
                    <Box key={tt.name} sx={{ mb: 0.5, '&:last-of-type': { mb: 0 } }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {tt.count}× {tt.name}
                        </Typography>
                        {opts.map((o) => (
                            <Typography
                                key={o.name}
                                variant="caption"
                                component="div"
                                sx={{ pl: 1 }}
                            >
                                {o.count}× {o.name}
                                {o.ageBand ? ` · ${o.ageBand}` : ''}
                            </Typography>
                        ))}
                    </Box>
                );
            })}
        </Box>
    );
}

const CheckinBookingRow = ({
    row,
    timeslotId,
    disabled,
    groupByAgeBand,
    expanded,
    onToggleExpand,
    editingNote,
    onNoteChange,
    onNoteBlur,
    reloadData,
    reloadTimeslot,
    canManageBookings,
    slotTemplate = [],
}) => {
    const { t } = useTranslation();
    const { notifyError, notifySuccess } = useNotify();
    const mixed = isMixedBooking(row.ticketTypes);
    const countEntries = getTicketCountEntries(row.ticketTypes, { groupByAgeBand });
    // The backend only includes sibling slots whose intervals overlap this view.
    const overlappingSibling =
        !!row.timeslot?.slotKey && Number(row.timeslotId) !== Number(timeslotId);
    // The booking's own timeslot id - the backend's check-in timeslot proof
    // and the booking-items filter expect it, not the page's slot.
    const rowTimeslotId = row.timeslotId;

    const firstCellSx = {
        borderLeft: MIXED_BORDER,
        borderLeftColor: mixed ? MIXED_COLOR : 'transparent',
    };

    return (
        <>
            <TableRow sx={{ backgroundColor: row.isCheckedIn ? '#e6faea' : 'white' }}>
                <TableCell align="center" sx={firstCellSx}>
                    <Checkbox
                        checked={row.isCheckedIn}
                        disabled={disabled}
                        onChange={async (ev) => {
                            try {
                                await editBookingCheckin(
                                    row.id,
                                    { isCheckedIn: ev.target.checked },
                                    { timeslotId: rowTimeslotId || undefined },
                                );
                                reloadData();
                                notifySuccess(t('bookings.notifications.updated'));
                            } catch (err) {
                                notifyError(err);
                                if (err?.response?.status === 409 && reloadTimeslot) {
                                    reloadTimeslot();
                                }
                            }
                        }}
                    />
                </TableCell>
                <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {mixed && (
                            <Tooltip
                                arrow
                                title={<MixedTooltipContent ticketTypes={row.ticketTypes} />}
                            >
                                <Layers
                                    fontSize="small"
                                    sx={{ color: MIXED_COLOR, cursor: 'help' }}
                                />
                            </Tooltip>
                        )}
                        <span>{`${row.firstName} ${row.lastName}`}</span>
                        {overlappingSibling && (
                            <Chip
                                size="small"
                                variant="outlined"
                                color="primary"
                                label={resolveSlotLabel(slotTemplate, row.timeslot)}
                            />
                        )}
                    </Box>
                    {row.hasPendingOrder ? (
                        <Box sx={{ mt: 0.75 }}>
                            <Chip
                                size="small"
                                label={
                                    row.pendingOrderAmount
                                        ? `${t('orderstatus.pending')}: ${formatCurrency(row.pendingOrderAmount / 100)}`
                                        : t('orderstatus.pending')
                                }
                                style={{ background: '#f0ad4e', color: '#fff' }}
                            />
                        </Box>
                    ) : row.status === BOOKING_STATUSES.PROCESSING ? (
                        <Box sx={{ mt: 0.75 }}>
                            <Chip
                                size="small"
                                label={t(`widgetReservationInfo.statusValues.${row.status}`)}
                                style={{
                                    background: BOOKING_STATUS_COLORS[row.status],
                                    color: '#fff',
                                }}
                            />
                        </Box>
                    ) : null}
                </TableCell>
                <TableCell>
                    {row.seatNames?.length > 0
                        ? groupSeatsByTable(row.seatNames).map(({ table, seats }) => (
                              <div key={table}>
                                  {table ? `${table}: ${seats.join(', ')}` : seats.join(', ')}
                              </div>
                          ))
                        : ''}
                </TableCell>
                <TableCell>
                    {countEntries.map(([label, count]) => (
                        <div key={label}>{`${count} ${label}`}</div>
                    ))}
                </TableCell>
                <TableCell>
                    {Array.isArray(row.note)
                        ? row.note.map((item) =>
                              item.label && item.value ? (
                                  <div key={item.label}>
                                      {item.label}: {item.value}
                                  </div>
                              ) : null,
                          )
                        : row.note || ''}
                </TableCell>
                <TableCell>
                    <TextField
                        disabled={disabled}
                        fullWidth
                        multiline
                        minRows={3}
                        value={editingNote ?? row.checkinNote ?? ''}
                        onChange={(e) => onNoteChange(row.id, e.target.value)}
                        onBlur={() => onNoteBlur(row.id)}
                        placeholder={t('bookingitems.columns.checkinNote')}
                    />
                </TableCell>
                <TableCell sx={{ width: 48, p: 0 }}>
                    <IconButton size="small" onClick={() => onToggleExpand(row.id)}>
                        {expanded ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                </TableCell>
                <TableCell align="right">
                    <Link to={row.reservationDetailsLink ? `${row.reservationDetailsLink}` : '#'}>
                        <Tooltip title={t('bookings.bookingDetails', { bookingId: row.id })}>
                            <IconButton>
                                <InfoIcon />
                            </IconButton>
                        </Tooltip>
                    </Link>
                    {canManageBookings && (
                        <Link to={`/bookings/edit/${row.id}`}>
                            <Tooltip title={t('common.edit')}>
                                <IconButton>
                                    <EditIcon />
                                </IconButton>
                            </Tooltip>
                        </Link>
                    )}
                </TableCell>
            </TableRow>
            {row.phone && (
                <TableRow>
                    <TableCell colSpan={COL_COUNT} sx={{ py: 0, border: 0 }}>
                        <Collapse in={expanded} unmountOnExit>
                            <Box
                                sx={{
                                    py: 1,
                                    px: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                }}
                            >
                                <Phone fontSize="small" color="action" />
                                <Typography variant="body2">{formatPhone(row.phone)}</Typography>
                            </Box>
                        </Collapse>
                    </TableCell>
                </TableRow>
            )}
            <ExpandableBookingItems
                expanded={expanded}
                bookingId={row.id}
                timeslotId={rowTimeslotId}
                disabled={disabled}
            />
        </>
    );
};

export default CheckinBookingRow;
